import React, { createContext, useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const AdminContext = createContext();

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error("useAdmin must be used within AdminProvider");
  }
  return context;
};

export const AdminProvider = ({ children }) => {
  const [adminData, setAdminData] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const navigate = useNavigate();

  // Check authentication on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = () => {
    try {
      const authStatus = localStorage.getItem("bsa_admin_auth");
      const storedAdminData = localStorage.getItem("bsa_admin_data");

      if (authStatus === "true" && storedAdminData) {
        const parsedData = JSON.parse(storedAdminData);

        // Check if session is still valid (24 hours)
        const loginTime = new Date(parsedData.loginTime);
        const now = new Date();
        const hoursSinceLogin = (now - loginTime) / (1000 * 60 * 60);

        if (hoursSinceLogin < 24) {
          setAdminData(parsedData);
          setIsAuthenticated(true);

          // Update last activity
          parsedData.lastActivity = now.toISOString();
          localStorage.setItem("bsa_admin_data", JSON.stringify(parsedData));
        } else {
          // Session expired
          logout();
        }
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const loginUrl =
        process.env.REACT_APP_ADMIN_LOGIN_URL || "/api/admin/login";

      // Demo mode - remove in production
      if (email === "admin@bsa.com" && password === "admin123") {
        const adminInfo = {
          email: email,
          name: "Admin User",
          role: "admin",
          loginTime: new Date().toISOString(),
          lastActivity: new Date().toISOString(),
        };

        localStorage.setItem("bsa_admin_auth", "true");
        localStorage.setItem("bsa_admin_data", JSON.stringify(adminInfo));

        setAdminData(adminInfo);
        setIsAuthenticated(true);

        return { success: true };
      }

      // Actual API call - uncomment for production
      /*
      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          return { success: false, error: 'Invalid email or password' };
        }
        throw new Error('Login failed');
      }
      
      const data = await response.json();
      
      if (data.success || data.email) {
        const adminInfo = {
          email: data.email || email,
          name: data.name || 'Admin',
          role: data.role || 'admin',
          loginTime: new Date().toISOString(),
          lastActivity: new Date().toISOString()
        };
        
        localStorage.setItem('bsa_admin_auth', 'true');
        localStorage.setItem('bsa_admin_data', JSON.stringify(adminInfo));
        
        setAdminData(adminInfo);
        setIsAuthenticated(true);
        
        return { success: true };
      }
      
      return { success: false, error: data.error || 'Invalid credentials' };
      */

      // If not demo credentials, return error
      return { success: false, error: "Invalid email or password" };
    } catch (error) {
      console.error("Login error:", error);
      return { success: false, error: "Login failed. Please try again." };
    }
  };

  const logout = () => {
    localStorage.removeItem("bsa_admin_auth");
    localStorage.removeItem("bsa_admin_data");
    setAdminData(null);
    setIsAuthenticated(false);
    setConversations([]);
  };

  const fetchConversations = async (page = 1) => {
    try {
      const url = process.env.REACT_APP_ADMIN_URL || "/api/admin/conversations";

      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ page }),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching conversations:", error);
      throw error;
    }
  };

  const value = {
    adminData,
    isAuthenticated,
    loading,
    conversations,
    setConversations,
    login,
    logout,
    checkAuth,
    fetchConversations,
  };

  return (
    <AdminContext.Provider value={value}>{children}</AdminContext.Provider>
  );
};

export default AdminContext;
